import { useState, Fragment, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import DynamicField from "components/form-fields/DynamicField/DynamicField"
import { PrimeButton } from "components-design-system"
import RJReadOnly from "../../../../components/RJReadOnly/RJReadOnly"
import EventItemHeader from "../EventItemHeader/EventItemHeader"
import EventCell from "../EventCell/EventCell"
import CellValidation from "../../../components/CellValidation/CellValidation"
import CharCounter from "components/CharCounter/CharCounter"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringPrintStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringPrintStore"

// utils
import { translateProps } from "../../helpers/helpers"
import { useInView } from "react-intersection-observer"

const EventItem = ({ event, eventListKey, rowIndex, events, adverseData, setAdverseData, schema }) => {
  const { ref: visibleRef, inView: inViewport } = useInView({
    /* Optional options */
    threshold: 0,
    triggerOnce: true,
  })

  const { isPrinting } = useAuthoringPrintStore(useShallow((state) => ({ isPrinting: state.isPrinting })))

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const { tableView } = useResultsTableViewStore()

  const [expand, setExpand] = useState(false)

  let fieldSchema = schema.properties[tableView].serious_adverse_event.properties

  const onChange = (e, key) => {
    let tempData = { ...adverseData }

    Object.keys(tempData).forEach((registry) => {
      let foundEventIndex = tempData[registry][eventListKey].findIndex((item) => item.id === event.id)
      let tempKey = eventListKey.slice(0, -1)
      let fieldSchema = schema.properties[registry][tempKey].properties

      if (foundEventIndex !== -1) {
        let adverseEvent = tempData[registry][eventListKey][foundEventIndex]
        if (key === "assessment_type") {
          if (registry !== tableView) adverseEvent[key] = translateProps[tableView][registry][key][e]
          else adverseEvent[key] = e
        } else if (key === "source_vocabulary" && (e === "MedDRA" || e === "SNOMED CT")) {
          adverseEvent[key] = e
          let foundEUDEventIndex = tempData["eudract"][eventListKey].findIndex((item) => item.id === event.id)
          if (foundEUDEventIndex !== -1) {
            tempData["eudract"][eventListKey][foundEUDEventIndex]["dictionary_name"] = e
            tempData["eudract"][eventListKey][foundEUDEventIndex]["other_dictionary_name"] = null
          }
        } else if (key === "source_vocabulary") {
          adverseEvent[key] = e
          let foundEUDEventIndex = tempData["eudract"][eventListKey].findIndex((item) => item.id === event.id)
          if (foundEUDEventIndex !== -1) {
            tempData["eudract"][eventListKey][foundEUDEventIndex]["dictionary_overridden"] = "Yes"
            tempData["eudract"][eventListKey][foundEUDEventIndex]["other_dictionary_name"] = e
            tempData["eudract"][eventListKey][foundEUDEventIndex]["dictionary_name"] = "Other (specify)"
          }
        } else if (key === "dictionary_overridden" && e === "No") {
          if (registry === "eudract") {
            adverseEvent["dictionary_name"] = null
            adverseEvent["other_dictionary_name"] = null
            adverseEvent[key] = e
            let foundCTGEventIndex = tempData["ctg"][eventListKey].findIndex((item) => item.id === event.id)

            if (foundCTGEventIndex !== -1) {
              tempData["ctg"][eventListKey][foundCTGEventIndex]["source_vocabulary"] =
                tempData["ctg"]["source_vocabulary"]
            }
          }
        } else if (fieldSchema[key]) {
          adverseEvent[key] = e
        }

        if (
          tableView === "eudract" &&
          adverseEvent["dictionary_name"] !== "Other (specify)" &&
          key !== "dictionary_overridden"
        ) {
          adverseEvent["other_dictionary_name"] = null
          let foundCTGEventIndex = tempData["ctg"][eventListKey].findIndex((item) => item.id === event.id)
          if (foundCTGEventIndex !== -1) {
            tempData["ctg"][eventListKey][foundCTGEventIndex]["source_vocabulary"] = adverseEvent["dictionary_name"]
          }
        }
      }
    })

    setAdverseData(tempData)
  }

  const displayCollapsed = () => {
    const collapsedViewKeys = ["term", "organ_system_name"]
    return (
      <>
        <EventItemHeader
          rowIndex={rowIndex}
          event={event}
          events={events}
          eventListKey={eventListKey}
          adverseData={adverseData}
          setAdverseData={setAdverseData}
        >
          {collapsedViewKeys.map((key) => {
            if (readOnly) {
              return (
                <RJReadOnly
                  key={key}
                  labelText={fieldSchema[key].title}
                  compare={false}
                  value={event[key]}
                />
              )
            }
            return (
              <div
                style={{ marginBottom: 12 }}
                key={key}
              >
                <DynamicField
                  type={fieldSchema[key].type}
                  fieldProps={{
                    label: fieldSchema[key].title,
                    readOnly,
                    options: fieldSchema[key].enum,
                    value: event[key],
                    onChange: (e) => onChange(e, key),
                  }}
                />
                {fieldSchema[key]?.charCountEnabled && formView?.id !== "global" && !readOnly && (
                  <div className="char-counter-container">
                    <CharCounter
                      value={event[key]}
                      maxChars={fieldSchema[key].maxChars}
                    />
                  </div>
                )}
                <CellValidation
                  table="adverse"
                  errorKey={`results_adverse_events_adverse_events_table_${tableView}_${eventListKey}_${
                    rowIndex + 1
                  }_${key}`}
                  style={{ marginBottom: 16 }}
                />
              </div>
            )
          })}
          <PrimeButton
            type="link"
            onClick={() => setExpand(true)}
            style={{ padding: 0 }}
          >
            Expand
          </PrimeButton>
        </EventItemHeader>
        {event?.values?.map((value, valueIndex) => {
          return (
            <EventCell
              key={`${tableView}-event-${event.id}-${valueIndex}`}
              event={event}
              value={value}
              valueIndex={valueIndex}
              eventListKey={eventListKey}
              adverseData={adverseData}
              setAdverseData={setAdverseData}
              schema={schema}
              rowIndex={rowIndex}
            />
          )
        })}
      </>
    )
  }

  const displayExpanded = () => {
    let fields = Object.keys(fieldSchema)
    if (tableView === "eudract") {
      if (event.dictionary_overridden === "No") {
        fields = Object.keys(fieldSchema).filter(
          (key) => key !== "dictionary_name" && key !== "dictionary_version" && key !== "other_dictionary_name"
        )
      }
    }

    return (
      <>
        <EventItemHeader
          events={events}
          event={event}
          rowIndex={rowIndex}
          adverseData={adverseData}
          setAdverseData={setAdverseData}
          eventListKey={eventListKey}
          style={{ width: 600 }}
        >
          {fields.map((key) => {
            let disabled = false
            if (
              key === "other_dictionary_name" &&
              tableView === "eudract" &&
              event.dictionary_overridden === "Yes" &&
              event.dictionary_name !== "Other (specify)"
            ) {
              disabled = true
            }

            return (
              <div
                key={`event-item-${event.id}-${key}`}
                style={{ marginBottom: 12 }}
              >
                {readOnly !== true ? (
                  <>
                    <DynamicField
                      type={fieldSchema[key].type}
                      fieldProps={{
                        label: fieldSchema[key].title,
                        readOnly,
                        disabled,
                        options: fieldSchema[key].enum,
                        value: event[key],
                        onChange: (e) => onChange(e, key),
                      }}
                    />
                    {fieldSchema[key]?.charCountEnabled && formView?.id !== "global" && !readOnly && (
                      <div className="char-counter-container">
                        <CharCounter
                          value={event[key]}
                          maxChars={fieldSchema[key].maxChars}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <RJReadOnly
                    labelText={fieldSchema[key].title}
                    compare={false}
                    value={event[key]}
                  />
                )}
                <CellValidation
                  table="adverse"
                  errorKey={`results_adverse_events_adverse_events_table_${tableView}_${eventListKey}_${
                    rowIndex + 1
                  }_${key}`}
                  style={{ marginBottom: 16 }}
                />
              </div>
            )
          })}
          <PrimeButton
            type="link"
            onClick={() => setExpand(false)}
            style={{ padding: 0 }}
          >
            Collapse
          </PrimeButton>
        </EventItemHeader>
        {event?.values?.map((value, valueIndex) => {
          return (
            <EventCell
              key={`${tableView}-event-${event.id}-${valueIndex}`}
              event={event}
              value={value}
              valueIndex={valueIndex}
              eventListKey={eventListKey}
              adverseData={adverseData}
              setAdverseData={setAdverseData}
              schema={schema}
              rowIndex={rowIndex}
            />
          )
        })}
      </>
    )
  }

  return (
    <tr ref={visibleRef}>
      {inViewport || events?.length < 10 || isPrinting ? (
        <>{expand ? displayExpanded() : displayCollapsed()}</>
      ) : (
        event.values.map((skey, i) => {
          return (
            <td
              key={`${skey}-${i}`}
              style={{ padding: 10 }}
            />
          )
        })
      )}
    </tr>
  )
}

export default EventItem
